import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { getTabForPath, getPathForTab, type TabKey } from './config/routes';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import LivePreviewPanel from './components/LivePreviewPanel';
import UpgradeModal from './components/UpgradeModal';
import CustomDomainModal from './components/CustomDomainModal';
import { PrivacyChoicesCard } from './components/PrivacyChoicesCard';
import { useConsent } from './context/ConsentContext';
import { PreviewProvider, usePreview } from './context/PreviewContext';
import { PlanProvider, usePlan } from './context/PlanContext';
import { SectionConfigProvider } from './context/SectionConfigContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import ShowcaseManager from './pages/ShowcaseManager';
import VideoManager from './pages/VideoManager';
import ImageManager from './pages/ImageManager';
import BannerManager from './pages/BannerManager';
import CategoryManager from './pages/CategoryManager';
import UserManager from './pages/UserManager';
import DomainManager from './pages/DomainManager';
import HeroSectionManager from './pages/HeroSectionManager';
import AboutSectionManager from './pages/AboutSectionManager';
import InfoKitManager from './pages/InfoKitManager';
import AwardsSectionManager from './pages/AwardsSectionManager';
import AwardAssetManager from './pages/AwardAssetManager';
import ServerImageManager from './pages/ServerImageManager';
import ContactWebsiteManager from './pages/ContactWebsiteManager';
import ContactSectionManager from './pages/ContactSectionManager';
import SiteBrandingManager from './pages/SiteBrandingManager';
import ToursEventManager from './pages/ToursEventManager';
import MasterThemeManager from './pages/MasterThemeManager';
import MasterPlatformsManager from './pages/MasterPlatformsManager';
import MasterCategoryManager from './pages/MasterCategoryManager';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
// Management-only website sections (siteType 'management' tenants)
import ArtistRosterManager from './pages/ArtistRosterManager';
import ServicesManager from './pages/ServicesManager';
import TestimonialsManager from './pages/TestimonialsManager';
import ClientsManager from './pages/ClientsManager';
import TeamManager from './pages/TeamManager';
import ManagerAccessPage from './pages/ManagerAccessPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import InvitePublicLandingPage from './pages/InvitePublicLandingPage';
import { OrganizationProvider, useOrganizations } from './context/OrganizationContext';
import UserPlatformsManager from './pages/UserPlatformsManager';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ProfilePage from './pages/ProfilePage';
import BillingPage from './pages/BillingPage';
import Onboarding from './pages/Onboarding';
import CompletionScreen from './components/onboarding/CompletionScreen';
import VerifiedScreen from './components/onboarding/VerifiedScreen';
import DeactivatedScreen from './components/onboarding/DeactivatedScreen';
import RejectedScreen from './components/onboarding/RejectedScreen';
import Overlay from './components/ui/overlay';
import NoAccessScreen from './components/NoAccessScreen';
import BottomNav from './components/BottomNav';
import axios from './utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import './App.css';
import { Toaster } from 'sonner';

interface User {
  _id: string;
  username: string;
  fullName?: string;
  user_type: string;
  email: string;
  domain: string;
  hasCompletedOnboarding?: boolean;
  approvalStatus?: string;
  profilePicture?: string;
}

interface Domain {
  _id: string;
  slug: string;
  url: string;
  hasCompletedOnboarding?: boolean;
  isPublished?: boolean;
  is_active?: boolean;
  approvalStatus?: string;
  approvalNote?: string;
  customDomain?: string | null;
  dnsVerified?: boolean;
  congratulationsShown?: boolean;
  /** 'artist' (default) | 'management' — switches the console nav set. */
  siteType?: 'artist' | 'management';
  organizationId?: string | null;
}

/**
 * Guard component that shows "Please Select a Domain" message
 * for super-admin users who haven't selected a domain yet.
 */
const DomainGuard: React.FC<{ user: User | null; selectedDomain: string; children: React.ReactNode }> = ({ user, selectedDomain, children }) => {
  if (!selectedDomain && user?.user_type === 'super-admin') {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-foreground mb-4">Please Select a Domain</h2>
        <p className="text-muted-foreground">Select a domain from the sidebar to manage its content</p>
      </div>
    );
  }
  return <>{children}</>;
};

// Inner App component that can use the preview context
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tab = getTabForPath(location.pathname) as TabKey;
  const setTab = useCallback((newTab: string) => {
    navigate(getPathForTab(newTab));
  }, [navigate]);
  const [token, setToken] = useState(() => localStorage.getItem('token') || sessionStorage.getItem('token') || '');
  // Keep ConsentContext aware of auth state so consent POSTs use the
  // authenticated endpoint (which attributes rows to the caller's userId)
  // once the JWT is present, and the public endpoint before login.
  const consentCtx = useConsent();
  useEffect(() => {
    consentCtx.setAuthenticated(!!token);
  }, [token, consentCtx]);
  // Organization context (read-only here). Derive accessible slugs for TopBar.
  const orgCtx = useOrganizations();
  const orgAccessibleSlugs = React.useMemo(
    () =>
      orgCtx.accessibleDomains
        .map((d) => d.slug)
        .filter((s, i, arr) => !!s && arr.indexOf(s) === i),
    [orgCtx.accessibleDomains],
  );  const [unopenedLeadsCount, setUnopnedLeadsCount] = useState<Record<string, number>>({});
  const [user, setUser] = useState<User | null>(() => {
    const u = localStorage.getItem('user') || sessionStorage.getItem('user');
    try {
      return u ? JSON.parse(u) : null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  });

  const [domains, setDomains] = useState<Domain[]>([]);
  const [domain, setDomain] = useState<Domain>({ _id: '', slug: '', url: '' });
  const [domainLoading, setDomainLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState<string>(() => {
    const u = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (u) {
      const parsedUser = JSON.parse(u);
      if (parsedUser.user_type === 'domain-admin') {
        return localStorage.getItem('selectedDomain') || sessionStorage.getItem('selectedDomain') || parsedUser.domain;
      }
      // For super-admin and admin, don't select any domain by default
      return '';
    }
    return '';
  });
  const [siteName, setSiteName] = useState<string>(() => {
    const u = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (u) {
      const parsedUser = JSON.parse(u);
      if (parsedUser.user_type === 'domain-admin') {
        return parsedUser.domain;
      }
      return 'Select Domain';
    }
    return 'Loading...';
  });

  useEffect(() => {
    if (!user) {
      setDomainLoading(false);
      return;
    }

    setDomainLoading(true);
    if (user.user_type === 'super-admin') {
      axios.get(BASE_URL + '/api/domain')
        .then(res => {
          // Super-admin domain dropdown should only list active domains.
          // Inactive (deactivated) domains are hidden from the selector to
          // keep the picker focused on live tenants; they remain manageable
          // via the Domain Manager page.
          const allDomains: Domain[] = (res.data || []).filter(
            (d: Domain) => d.is_active !== false
          );
          setDomains(allDomains);
          setSelectedDomain('');
          localStorage.removeItem('selectedDomain');
          setSiteName('Select Domain');
        })
        .catch(() => {
          setSiteName('Error Loading Domains');
        })
        .finally(() => setDomainLoading(false));
    } else if (user.user_type === 'domain-admin') {
      // Phase 5 — a manager invited into an organization has no owned
      // domain (`user.domain` is null). Skip the owned-domain fetch so we
      // don't hit /api/domain/slug/null; org-mediated domains are still
      // loaded by OrganizationProvider and surface in the topbar.
      if (!user.domain) {
        setDomains([]);
        setSelectedDomain('');
        localStorage.removeItem('selectedDomain');
        sessionStorage.removeItem('selectedDomain');
        setSiteName('Select Domain');
        setDomainLoading(false);
      } else {
      // Fetch domain details for domain-admin by slug
      axios.get(BASE_URL + `/api/domain/slug/${user.domain}`)
        .then(res => {
          setDomains([res.data]);
        })
        .catch(() => {
          console.error('Error fetching domain details');
        })
        .finally(() => setDomainLoading(false));
      setSelectedDomain(user.domain);
      localStorage.setItem('selectedDomain', user.domain);
      setSiteName(user.domain);
      }
    } else {
      setSelectedDomain('');
      localStorage.removeItem('selectedDomain');
      setSiteName('Select Domain');
      setDomainLoading(false);
    }
    // eslint-disable-next-line
  }, [user]);

  // Socket.io connection
  const socketRef = useRef<ReturnType<typeof io> | null>(null);

  useEffect(() => {
    // Phase 0.5: the backend now requires a valid credential at the socket
    // handshake. Re-read the JWT from storage at connect time so we always
    // send a fresh value (avoids stale-closure issues from this effect's []
    // dep array). If no token is present we still attempt to connect — the
    // server will reject and `auth_error` will fire, which is the desired
    // behavior (it surfaces the missing-auth state to the user).
    const token =
      typeof window !== 'undefined'
        ? (localStorage.getItem('token') || sessionStorage.getItem('token') || undefined)
        : undefined;

    const socketInstance = io(BASE_URL, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      auth: {
        token,
        domain: selectedDomain || undefined
      }
    });

    socketRef.current = socketInstance;

    socketInstance.on('connect', () => {
      console.log('Connected to server via WebSocket');
      // Join domain room on connect/reconnect
      if (selectedDomain) {
        socketInstance.emit('join-domain', selectedDomain);
      }
    });

    socketInstance.on('auth_error', (data: { reason?: string }) => {
      console.warn('[Socket] auth_error:', data?.reason);
    });

    socketInstance.on('join_denied', (data: { slug?: string; reason?: string }) => {
      console.warn('[Socket] join_denied:', data?.reason, 'slug=', data?.slug);
    });

    socketInstance.on('contact:update', (data: { domain_slug?: string }) => {
      console.log('New submission received:', data);
      if (data.domain_slug) {
        const slug = data.domain_slug;
        setUnopnedLeadsCount(prev => ({
          ...prev,
          [slug]: (prev[slug] || 0) + 1
        }));
      }
    });

    socketInstance.on('domain:approved', (data: { approvalStatus: string; slug: string }) => {
      setDomain(prev => ({ ...prev, approvalStatus: data.approvalStatus as 'approved' }));
      setDomains(prev => prev.map(d => d.slug === data.slug ? { ...d, approvalStatus: data.approvalStatus as 'approved' } : d));
    });

    socketInstance.on('domain:rejected', (data: { approvalStatus: string; approvalNote: string; slug: string }) => {
      setDomain(prev => ({ ...prev, approvalStatus: data.approvalStatus as 'rejected', approvalNote: data.approvalNote }));
      setDomains(prev => prev.map(d => d.slug === data.slug ? { ...d, approvalStatus: data.approvalStatus as 'rejected', approvalNote: data.approvalNote } : d));
    });

    // When any admin activates a theme (including from another tab or concurrent admin),
    // refresh the theme grid so the Active badge moves to the correct card.
    socketInstance.on('theme:update', (data: { _id?: string }) => {
      if (data?._id) {
        previewContext?.triggerThemeActivated(data._id);
      }
    });

    socketInstance.on('disconnect', () => {
      console.log('Disconnected from server');
    });

    return () => {
      socketInstance.disconnect();
      socketRef.current = null;
    };

  }, []);

  // Switch socket room when selected domain changes
  useEffect(() => {
    if (socketRef.current?.connected && selectedDomain) {
      socketRef.current.emit('join-domain', selectedDomain);
    }
  }, [selectedDomain]);

  // Track last fetched domain to prevent duplicate unopened-count calls
  const lastFetchedDomainRef = useRef<string>('');

useEffect(() => {
  if (!selectedDomain) {
    setSiteName('Select Domain');
    localStorage.removeItem('selectedDomain');
    lastFetchedDomainRef.current = '';
  } else if (domains.length > 0) {
    const match = domains.find(dom => dom.slug === selectedDomain);
    if (match) {
      setSiteName(match.slug);
      localStorage.setItem('selectedDomain', selectedDomain);
      setDomain(match);
    } else if (
      user?.user_type === 'domain-admin' &&
      orgCtx.accessibleDomains.some((d) => d.slug === selectedDomain)
    ) {
      // Phase 4 — the user picked an org-accessible domain that isn't in
      // the locally-cached `domains` (which only holds owned domains for a
      // domain-admin). Fetch the full Domain doc using the X-Domain header
      // so the topbar label and live-preview panel resolve correctly, then
      // merge into the cache.
      localStorage.setItem('selectedDomain', selectedDomain);
      axios
        .get(BASE_URL + `/api/domain/slug/${selectedDomain}`)
        .then((res) => {
          const fetched = res.data;
          if (!fetched || fetched.slug !== selectedDomain) return;
          setDomains((prev) =>
            prev.some((d) => d.slug === fetched.slug) ? prev : [...prev, fetched],
          );
          setDomain(fetched);
          setSiteName(fetched.slug);
        })
        .catch(() => {
          // Non-fatal — leave the user on a degraded state but don't crash.
          setDomain({ _id: '', slug: selectedDomain, url: '' });
          setSiteName(selectedDomain);
        });
    } else {
      // Unknown slug — preserve legacy fallback behavior.
      localStorage.setItem('selectedDomain', selectedDomain);
      setDomain({ _id: '', slug: '', url: '' });
    }
    // Only fetch unopened count if domain actually changed
    if (lastFetchedDomainRef.current !== selectedDomain) {
      lastFetchedDomainRef.current = selectedDomain;
      axios.get(`/api/contact-website/${selectedDomain}/unopened/count`)
        .then(res => {
          setUnopnedLeadsCount(prev => ({
            ...prev,
            [selectedDomain]: res.data.unopenedCount
          }));
        })
        .catch(() => {
          setUnopnedLeadsCount(prev => ({
            ...prev,
            [selectedDomain]: 0
          }));
        });
    }


  }
}, [selectedDomain, domains, orgCtx.accessibleDomains, user?.user_type]);

// Phase D1 — a direct-grant Manager has user_type='domain-admin' but no
// owned `user.domain`. Their only access comes from accessibleDomains
// (projected from OrganizationManagerAssignment). When that resolves
// with at least one slug, auto-select the first one so the UI doesn't
// boot into an empty "Select Domain" state. Runs only when nothing is
// currently selected, so it doesn't fight a user's manual choice.
useEffect(() => {
  if (!user) return;
  if (user.user_type !== 'domain-admin') return;
  if (user.domain) return;
  if (selectedDomain) return;
  if (orgCtx.loading) return;
  const slugs = orgCtx.accessibleDomains.map((d) => d.slug).filter(Boolean);
  if (slugs.length === 0) return;
  setSelectedDomain(slugs[0]);
  localStorage.setItem('selectedDomain', slugs[0]);
}, [user, selectedDomain, orgCtx.accessibleDomains, orgCtx.loading]);

// If the currently-selected slug is no longer in the manager's accessible
// set (artist or management company just revoked access), drop it. The
// auto-pick effect above will then either pick the next available slug or
// leave selection empty so the no-access screen / org redirect can render.
useEffect(() => {
  if (!user) return;
  if (user.user_type !== 'domain-admin') return;
  if (user.domain) return; // owners keep their owned slug
  if (!selectedDomain) return;
  if (orgCtx.loading) return;
  const accessibleSet = new Set(orgCtx.accessibleDomains.map((d) => d.slug));
  if (!accessibleSet.has(selectedDomain)) {
    setSelectedDomain('');
    try {
      localStorage.removeItem('selectedDomain');
      sessionStorage.removeItem('selectedDomain');
    } catch {}
  }
}, [user, selectedDomain, orgCtx.accessibleDomains, orgCtx.loading]);

// When an org-mediated slug is selected (manager has no owned domain),
// the `domains` state from the owned-domain fetch above is empty, so
// `currentDomainObj` is null and the LivePreviewPanel can't build a URL.
// Fetch the full Domain record by slug and merge it in.
useEffect(() => {
  if (!selectedDomain) return;
  if (domains.some((d) => d.slug === selectedDomain)) return;
  let cancelled = false;
  axios
    .get(`${BASE_URL}/api/domain/slug/${selectedDomain}`)
    .then((res) => {
      if (cancelled || !res.data) return;
      setDomains((prev) =>
        prev.some((d) => d.slug === res.data.slug) ? prev : [...prev, res.data],
      );
      setSiteName(res.data.slug);
    })
    .catch(() => {
      // Non-fatal — preview will simply remain unavailable.
    });
  return () => {
    cancelled = true;
  };
}, [selectedDomain, domains]);

const handleLogin = (jwt: string, user: User, rememberMe: boolean = true) => {
  setToken(jwt);
  setUser(user);

  // Persist to the correct storage based on remember preference
  const storage = rememberMe ? localStorage : sessionStorage;
  // Clear the other storage to avoid stale data
  const otherStorage = rememberMe ? sessionStorage : localStorage;
  otherStorage.removeItem('token');
  otherStorage.removeItem('user');
  otherStorage.removeItem('selectedDomain');

  storage.setItem('token', jwt);
  storage.setItem('user', JSON.stringify(user));

  // Notify app-level providers (OrganizationProvider) that auth changed.
  // MUST be fired AFTER storage.setItem so the App-level sync handler
  // reads the new token, not the stale (empty) one. Otherwise the org
  // context never re-fetches on login and the no-access overlay flashes
  // until a hard refresh reads the token at mount.
  try { window.dispatchEvent(new Event('auth-changed')); } catch {}

  if (user.user_type === 'domain-admin') {
    if (user.domain) {
      setSelectedDomain(user.domain);
      storage.setItem('selectedDomain', user.domain);
      setSiteName(user.domain);
    } else {
      // Phase 5 — domain-less manager (org-invited). No owned tenant; the
      // app lands on /organizations via the route guard below.
      setSelectedDomain('');
      storage.removeItem('selectedDomain');
      setSiteName('Select Domain');
    }
    // Onboarding overlay will show automatically if not completed
  } else {
    // For admin and super-admin, start with no domain selected
    setSelectedDomain('');
    localStorage.removeItem('selectedDomain');
    sessionStorage.removeItem('selectedDomain');
    setSiteName('Select Domain');
  }

  // Phase 4 — if the user arrived from /invite/:token while logged out,
  // pick up where they left off and accept the invitation.
  try {
    const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
    if (pendingInviteToken) {
      navigate(`/invite/${pendingInviteToken}`, { replace: true });
    }
  } catch {}
};

const authPage = location.pathname === '/signup' ? 'signup' : 'login';

// Merge profile edits (fullName / profilePicture) into the in-memory user
// and whichever storage currently holds it, so TopBar & co. update live.
const handleUserUpdated = (updates: Partial<User>) => {
  setUser(prev => {
    if (!prev) return prev;
    const next = { ...prev, ...updates };
    for (const s of [localStorage, sessionStorage]) {
      if (s.getItem('user')) s.setItem('user', JSON.stringify(next));
    }
    return next;
  });
};

const handleLogout = () => {
  setToken('');
  setUser(null);
  setSelectedDomain('');
  // Clear both storages
  for (const s of [localStorage, sessionStorage]) {
    s.removeItem('token');
    s.removeItem('user');
    s.removeItem('selectedDomain');
  }
  localStorage.removeItem('onboarding_progress');
  localStorage.removeItem('previewPanelOpen');
  localStorage.removeItem('previewPanelExpanded');
  localStorage.removeItem('sidebarCollapsedSections');
  try { window.dispatchEvent(new Event('auth-changed')); } catch {}
  navigate('/login', { replace: true });
};

// Memoize the unopened count callback to prevent infinite loops
const handleUnopendedCountChange = useCallback((count: number) => {
  setUnopnedLeadsCount(prev => ({
    ...prev,
    [selectedDomain]: count
  }));
}, [selectedDomain]);

// Scroll to top whenever tab changes
// Scroll to top whenever tab changes
useEffect(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, [tab]);

// Mobile sidebar state
const [sidebarOpen, setSidebarOpen] = useState(false);

// Bump this to force Dashboard to re-fetch after the verified screen is dismissed
const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

const refreshDomainAndDashboard = () => {
  setDashboardRefreshKey(k => k + 1);
  if (user?.domain) {
    axios.get(BASE_URL + `/api/domain/slug/${user.domain}`)
      .then(res => {
        setDomain(res.data);
        setDomains(prev => prev.map(d => d.slug === user.domain ? res.data : d));
      })
      .catch(() => {});
  }
};

// Track account deactivation detected from 403 responses
const [deactivatedMessage, setDeactivatedMessage] = useState<string | null>(null);

useEffect(() => {
  const handler = (e: Event) => {
    const msg = (e as CustomEvent).detail?.message || 'Your account has been deactivated.';
    setDeactivatedMessage(msg);
  };
  window.addEventListener('account-deactivated', handler);
  return () => window.removeEventListener('account-deactivated', handler);
}, []);

// Handle 401 auth-expired from axios interceptor (state-based, no full page reload)
useEffect(() => {
  const handler = () => {
    setToken('');
    setUser(null);
    setSelectedDomain('');
    navigate('/login', { replace: true });
  };
  window.addEventListener('auth-expired', handler);
  return () => window.removeEventListener('auth-expired', handler);
}, []);

// Close sidebar on route change (mobile)
useEffect(() => {
  setSidebarOpen(false);
}, [location.pathname]);

// Determine if preview should be shown for current tab
const shouldShowPreview = useMemo(() => {
  const previewEnabledTabs = [
    'dashboard', 'banner', 'heroSection', 'aboutSection', 'infoKit', 'awardsSection',
    'music', 'video', 'image', 'toursEvents', 'category', 'platforms', 'siteBranding', 'contactSection',
    // Management-only section managers
    'services', 'artistRoster', 'testimonials', 'clients', 'team',
  ];
  return previewEnabledTabs.includes(tab) && !!selectedDomain;
}, [tab, selectedDomain]);


// Get current domain object
const currentDomainObj = useMemo(() => {
  return domains.find(d => d.slug === selectedDomain) || null;
}, [domains, selectedDomain]);

// Site type per accessible slug — badges management websites in the switcher.
// Sources: the org-accessible list (company admins) + the fetched Domain docs
// (super-admin's full list / selected domain).
const siteTypeBySlug = React.useMemo(() => {
  const map: Record<string, 'artist' | 'management'> = {};
  for (const d of orgCtx.accessibleDomains) map[d.slug] = d.siteType || 'artist';
  for (const d of domains) map[d.slug] = d.siteType || 'artist';
  return map;
}, [orgCtx.accessibleDomains, domains]);

// Site type of the SELECTED website — drives the sidebar nav set (management
// websites get roster/testimonials/clients/team instead of artist sections).
const currentSiteType: 'artist' | 'management' = currentDomainObj?.siteType
  || orgCtx.accessibleDomains.find((d) => d.slug === selectedDomain)?.siteType
  || 'artist';

// Preview panel state (from context, persisted in localStorage)
const previewContext = usePreview();
const { isPreviewOpen, setIsPreviewOpen } = previewContext;

// Persist preview open state
useEffect(() => {
  localStorage.setItem('previewPanelOpen', JSON.stringify(isPreviewOpen));
}, [isPreviewOpen]);

const togglePreview = useCallback(() => {
  setIsPreviewOpen(prev => !prev);
}, []);

// Auto-open preview panel when triggered from other components (e.g. SiteBrandingManager Preview button)
const prevOpenPanelTriggerRef = useRef(previewContext.openPanelTrigger);
useEffect(() => {
  if (previewContext.openPanelTrigger !== prevOpenPanelTriggerRef.current) {
    prevOpenPanelTriggerRef.current = previewContext.openPanelTrigger;
    setIsPreviewOpen(true);
  }
}, [previewContext.openPanelTrigger]);

// Derived booleans for overlay scroll lock (computed before early returns so the hook always runs)
// NOTE: managers (artist-direct or org-mediated) are also persisted as
// `domain-admin` but have NO `user.domain` of their own. They must NEVER
// see the artist-only approval/verified gates, which are tied to the
// owner's domain lifecycle. Key off `user.domain` (the owned slug), not
// just user_type.
const _showApprovalGate =
  user?.user_type === 'domain-admin' &&
  !!user?.domain &&
  !!domain?.slug &&
  !!domain?.hasCompletedOnboarding &&
  domain?.approvalStatus !== 'approved';
const _showVerifiedScreen =
  user?.user_type === 'domain-admin' &&
  !!user?.domain &&
  !!domain?.slug &&
  !!domain?.hasCompletedOnboarding &&
  domain?.approvalStatus === 'approved' &&
  !domain?.isPublished;

// Lock body scroll when approval/verified/deactivated overlay is visible
useEffect(() => {
  if (_showApprovalGate || _showVerifiedScreen || deactivatedMessage) {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }
}, [_showApprovalGate, _showVerifiedScreen, deactivatedMessage]);

if (!token) {
  // Phase 4 — if an unauthenticated user lands on /invite/:token,
  // surface the public invite landing page directly. The page itself
  // bounces to /login (with the token stashed) when the invitee already
  // has an account, or completes signup+accept inline when they don't.
  const inviteMatch = location.pathname.match(/^\/invite\/([^/]+)/);
  if (inviteMatch) {
    try { sessionStorage.setItem('pendingInviteToken', inviteMatch[1]); } catch {}
    return (
      <div className="min-h-screen bg-background">
        <InvitePublicLandingPage onLogin={handleLogin} />
        <PrivacyChoicesCard />
      </div>
    );
  }
  if (location.pathname === '/') {
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="min-h-screen bg-background">
      {authPage === 'login'
        ? <Login onLogin={handleLogin} onSwitchToSignup={() => navigate('/signup', { replace: true })} />
        : <Signup onLogin={handleLogin} onSwitchToLogin={() => navigate('/login', { replace: true })} />
      }
      <PrivacyChoicesCard />
    </div>
  );
}

// Wait for domain data to load before rendering dashboard (prevents flash)
if (domainLoading && user?.user_type === 'domain-admin') {
  return (
    <div className="flex items-center justify-center h-screen text-sm text-muted-foreground">
      Loading your workspace...
    </div>
  );
}

// Domain selection is now handled in the sidebar
// Calculate main content width based on preview state
const showPreviewPanel = shouldShowPreview && isPreviewOpen;

// Show onboarding overlay for domain-admin users whose domain hasn't completed it
const showOnboarding =
  user?.user_type === 'domain-admin' &&
  !!user?.domain &&
  domain?.slug &&
  !domain?.hasCompletedOnboarding;

// Show approval gate for domain-admin whose onboarding is done but not approved
const showApprovalGate =
  user?.user_type === 'domain-admin' &&
  !!user?.domain &&
  domain?.slug &&
  domain?.hasCompletedOnboarding &&
  domain?.approvalStatus !== 'approved';

// Show verified screen once when status transitions to approved
const showVerifiedScreen =
  user?.user_type === 'domain-admin' &&
  !!user?.domain &&
  domain?.slug &&
  domain?.hasCompletedOnboarding &&
  domain?.approvalStatus === 'approved' &&
  !domain?.isPublished;

// Access-scope summary for domain-admin without an owned domain. A manager
// can lose all access at any time (artist revokes the artist-direct grant,
// or the management company is deactivated). When the merged accessible-slug
// set drops to zero AND they belong to no management company, there is
// nothing to show — render the no-access screen instead of stranding them
// on /organizations.
const isDomainlessManager =
  user?.user_type === 'domain-admin' && !user?.domain;
const totalAccessibleSlugs = orgAccessibleSlugs.length;
// `hasManagementRole` (owner/admin in some org) gates the management-company
// workspace. A scoped Member (= artist manager) belongs to an org in the DB
// sense but has no business in /organizations — they only manage specific
// artist domains. So the no-access screen and the /organizations fallback
// both key off `hasManagementRole`, NOT `hasOrganizations`.
const showNoAccess =
  isDomainlessManager &&
  !orgCtx.loading &&
  totalAccessibleSlugs === 0 &&
  !orgCtx.hasManagementRole;

return (
  <PlanProvider domain={selectedDomain} userType={user?.user_type} socketRef={socketRef}>    <SectionConfigProvider selectedDomain={selectedDomain}>  <div className="min-h-screen bg-muted/50">
    {/* Account deactivated overlay */}
    {deactivatedMessage && (
      <Overlay open z={200}>
        <DeactivatedScreen
          message={deactivatedMessage}
          onSignOut={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('selectedDomain');
            localStorage.removeItem('onboarding_progress');
            window.location.href = '/login';
          }}
        />
      </Overlay>
    )}
    {/* No-access overlay — manager whose every grant was revoked. Sits
        above all routed content so any stale page they were on is hidden. */}
    {showNoAccess && !deactivatedMessage && (
      <Overlay open z={190}>
        <NoAccessScreen
          onSignOut={handleLogout}
          onRefresh={() => orgCtx.refresh()}
        />
      </Overlay>
    )}
    {/* Onboarding overlay rendered on top of dashboard */}
    {showOnboarding && (
      <Onboarding
        user={user}
        onComplete={() => {
          // Update domain state to mark onboarding as completed + pending
          setDomain(prev => ({ ...prev, hasCompletedOnboarding: true, approvalStatus: 'pending' }));
          setDomains(prev => prev.map(d => d.slug === selectedDomain ? { ...d, hasCompletedOnboarding: true, approvalStatus: 'pending' } : d));
          const updatedUser = { ...user!, hasCompletedOnboarding: true, approvalStatus: 'pending' };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }}
        onLogout={handleLogout}
      />
    )}
    {/* Pending approval overlay */}
    {showApprovalGate && (domain?.approvalStatus === 'pending' || !domain?.approvalStatus) && (
      <Overlay open z={200}>
        <CompletionScreen
          onLogout={handleLogout}
          onStatusRefresh={async () => {
            if (user?.domain) {
              const res = await axios.get(BASE_URL + `/api/domain/slug/${user.domain}`);
              setDomain(res.data);
              setDomains(prev => prev.map(d => d.slug === user.domain ? res.data : d));
            }
          }}
        />
      </Overlay>
    )}
    {/* Rejected overlay */}
    {showApprovalGate && domain?.approvalStatus === 'rejected' && (
      <Overlay open z={200}>
        <RejectedScreen
          approvalNote={domain?.approvalNote}
          onSignOut={handleLogout}
        />
      </Overlay>
    )}
    {/* Verified/congratulations overlay */}
    {showVerifiedScreen && (
      <Overlay open z={200}>
        <VerifiedScreen
          onGoToDashboard={() => {
            setDomain(prev => ({ ...prev, isPublished: true }));
            setDomains(prev => prev.map(d => d.slug === selectedDomain ? { ...d, isPublished: true } : d));
            refreshDomainAndDashboard();
          }}
          onLogout={handleLogout}
        />
      </Overlay>
    )}
    <Sidebar
      user={user}
      domains={domains}
      selectedDomain={selectedDomain}
      setSelectedDomain={setSelectedDomain}
      tab={tab}
      setTab={setTab}
      siteName={siteName}
      handleLogout={handleLogout}
      mobileOpen={sidebarOpen}
      onMobileClose={() => setSidebarOpen(false)}
      hasOrganizations={orgCtx.hasOrganizations}
      siteType={currentSiteType}
    />
    <TopBar
      user={user}
      domains={domains}
      selectedDomain={selectedDomain}
      setSelectedDomain={setSelectedDomain}
      siteName={siteName}
      handleLogout={handleLogout}
      setTab={setTab}
      currentTab={tab}
      unopenedLeadsCount={unopenedLeadsCount[selectedDomain] || 0}
      isPreviewOpen={isPreviewOpen}
      onTogglePreview={togglePreview}
      showPreviewToggle={shouldShowPreview}
      onHamburgerClick={() => setSidebarOpen(true)}
      currentDomain={domain}
      siteTypeBySlug={siteTypeBySlug}
      orgAccessibleSlugs={orgAccessibleSlugs}
      hasOrganizations={orgCtx.hasOrganizations}
      hasManagementRole={orgCtx.hasManagementRole}
    />
    <main 
      className={`ml-0 lg:ml-64 mt-[60px] lg:mt-16 p-4 pb-20 lg:pb-8 lg:p-8 transition-all duration-300 ${
        showPreviewPanel ? 'lg:mr-[35%] min-[1800px]:mr-[600px]' : ''
      }`}
    >
      <div className="max-w-screen-md mx-auto lg:max-w-none">
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
          <Routes>
              <Route path="/" element={
                (!selectedDomain && user?.user_type === 'super-admin') ? (
                  <SuperAdminDashboard user={user} setTab={setTab as (tab: string) => void} />
                ) : (!selectedDomain && isDomainlessManager) ? (
                  // Domain-less manager. Decide where to send them based on
                  // current scope:
                  //  • still loading → spinner (avoid flicker to the wrong
                  //    surface during the initial /me/accessible-domains fetch)
                  //  • zero slugs + zero management companies → the no-access
                  //    overlay above is already up; render nothing here
                  //  • zero slugs + at least one company → management workspace
                  //  • ≥1 slug → the auto-pick effect will set selectedDomain
                  //    on the next render; show a spinner in the meantime.
                  orgCtx.loading || totalAccessibleSlugs > 0 ? (
                    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                      Loading your workspace…
                    </div>
                  ) : showNoAccess ? null : orgCtx.hasManagementRole ? (
                    <Navigate to="/organizations" replace />
                  ) : null
                ) : (
                  <Dashboard domain={selectedDomain} user={user} setTab={setTab as (tab: string) => void} unopenedLeadsCount={unopenedLeadsCount[selectedDomain] || 0} refreshKey={dashboardRefreshKey} />
                )
              } />
              <Route path="/banner" element={<DomainGuard user={user} selectedDomain={selectedDomain}><BannerManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/hero-section" element={<DomainGuard user={user} selectedDomain={selectedDomain}><HeroSectionManager domain={selectedDomain} siteType={currentSiteType} /></DomainGuard>} />
              <Route path="/about-section" element={<DomainGuard user={user} selectedDomain={selectedDomain}><AboutSectionManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/info-kit" element={<DomainGuard user={user} selectedDomain={selectedDomain}><InfoKitManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/awards-section" element={<DomainGuard user={user} selectedDomain={selectedDomain}><AwardsSectionManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/award-assets" element={<AwardAssetManager />} />
              <Route path="/server-images" element={<ServerImageManager />} />
              <Route path="/showcase" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ShowcaseManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/videos" element={<DomainGuard user={user} selectedDomain={selectedDomain}><VideoManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/images" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ImageManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/categories" element={<DomainGuard user={user} selectedDomain={selectedDomain}><CategoryManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/platforms" element={<DomainGuard user={user} selectedDomain={selectedDomain}><UserPlatformsManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/contact-website" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ContactWebsiteManager domain={selectedDomain} onUnopendedCountChange={handleUnopendedCountChange} /></DomainGuard>} />
              <Route path="/contact-section" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ContactSectionManager /></DomainGuard>} />
              {/* Management-only website sections */}
              <Route path="/artist-roster" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ArtistRosterManager /></DomainGuard>} />
              <Route path="/services" element={currentSiteType !== 'management' && (currentDomainObj || orgCtx.accessibleDomains.some((d) => d.slug === selectedDomain)) ? <Navigate to="/" replace /> : <DomainGuard user={user} selectedDomain={selectedDomain}><ServicesManager key={selectedDomain} /></DomainGuard>} />
              <Route path="/testimonials" element={<DomainGuard user={user} selectedDomain={selectedDomain}><TestimonialsManager /></DomainGuard>} />
              <Route path="/clients" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ClientsManager /></DomainGuard>} />
              <Route path="/team" element={<DomainGuard user={user} selectedDomain={selectedDomain}><TeamManager /></DomainGuard>} />
              <Route path="/site-branding" element={<DomainGuard user={user} selectedDomain={selectedDomain}><SiteBrandingManager domain={selectedDomain} user={user} domainObj={currentDomainObj ?? { _id: '', slug: '', url: '' }}  setDomain={setDomain} /></DomainGuard>} />
              <Route path="/tours-events" element={<DomainGuard user={user} selectedDomain={selectedDomain}><ToursEventManager domain={selectedDomain} /></DomainGuard>} />
              <Route path="/users" element={<UserManager />} />
              <Route path="/domains" element={<DomainManager />} />
              <Route path="/master-themes" element={<MasterThemeManager />} />
              <Route path="/master-platforms" element={<MasterPlatformsManager />} />
              <Route path="/master-categories" element={<MasterCategoryManager />} />
              <Route path="/profile" element={<ProfilePage onLogout={handleLogout} onUserUpdated={handleUserUpdated} />} />
              {/* Billing is unavailable on management websites (admin-granted
                  entitlements; a separate payment model is future work) —
                  the route falls through to the `*` redirect below. */}
              {currentSiteType !== 'management' && (
                <Route path="/billing" element={<DomainGuard user={user} selectedDomain={selectedDomain}><BillingPage /></DomainGuard>} />
              )}
              <Route path="/organizations" element={<OrganizationsPage />} />
              <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
              <Route path="/manager-access" element={<ManagerAccessPage domainSlug={selectedDomain || undefined} userType={user?.user_type} />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              {/* Catch-all: redirect unknown routes to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </div>
      </div>
    </main>
    
    {/* Live Preview Panel */}
    {shouldShowPreview && (
      <LivePreviewPanel
        domain={currentDomainObj ?? domain}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onToggle={togglePreview}
        currentSection={tab}
        refreshKey={dashboardRefreshKey}
      />
    )}

    {/* Bottom Navigation - mobile only */}
    <BottomNav selectedDomain={selectedDomain} siteType={currentSiteType} />
    
    {/* Global Upgrade Modal */}
    <GlobalUpgradeModal />

    {/* Global Custom Domain Modal (auto-opens after a successful Pro upgrade) */}
    <GlobalCustomDomainModal currentDomainObj={currentDomainObj} />

    {/* Bottom-right privacy card — reachable from the sidebar Privacy Choices link */}
    <PrivacyChoicesCard />
  </div>
  </SectionConfigProvider>
  </PlanProvider>
);
};

// Global Upgrade Modal Component that uses PlanContext
const GlobalUpgradeModal: React.FC = () => {
  const { showUpgradeModal, setShowUpgradeModal, upgradeModalResource } = usePlan();
  return (
    <UpgradeModal
      open={showUpgradeModal}
      onOpenChange={setShowUpgradeModal}
      resourceType={upgradeModalResource}
    />
  );
};

// Global Custom Domain Modal — listens to PlanContext.openCustomDomainModal().
// `currentDomainObj` is required because the API needs the Mongo `_id`, not the slug.
const GlobalCustomDomainModal: React.FC<{ currentDomainObj: Domain | null }> = ({
  currentDomainObj,
}) => {
  const { showCustomDomainModal, setShowCustomDomainModal } = usePlan();
  if (!currentDomainObj || !currentDomainObj._id) return null;
  return (
    <CustomDomainModal
      open={showCustomDomainModal}
      onOpenChange={setShowCustomDomainModal}
      domainId={currentDomainObj._id}
      initialCustomDomain={currentDomainObj.customDomain ?? null}
    />
  );
};

// Main App component wrapped with PreviewProvider
const App: React.FC = () => {
  // Pull token here so OrganizationProvider can re-fetch on login/logout without
  // requiring AppContent to mount inside its own provider.
  const [appToken, setAppToken] = useState(
    () => localStorage.getItem('token') || sessionStorage.getItem('token') || '',
  );
  // Sync provider's token with auth-storage changes (login/logout in AppContent
  // touch storage; we observe it via the storage event + a custom event).
  useEffect(() => {
    const sync = () => {
      setAppToken(localStorage.getItem('token') || sessionStorage.getItem('token') || '');
    };
    window.addEventListener('storage', sync);
    window.addEventListener('auth-changed', sync as EventListener);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('auth-changed', sync as EventListener);
    };
  }, []);
  return (
    <PreviewProvider>
      <Toaster position="bottom-right" richColors />
      <OrganizationProvider token={appToken || null}>
        <AppContent />
      </OrganizationProvider>
    </PreviewProvider>
  );
};

export default App;
